import React, { useState, useEffect } from 'react';
import Navbar from '../layout/Navbar';
import { ArrowRight, Award, Users, Factory, TrendingUp, CheckCircle, Phone, Mail, MapPin, Lightbulb, Goal, Rocket, HandCoins, ChevronLeft, ChevronRight } from 'lucide-react';
import Footer from '../layout/Footer';
import steel_plants from '../assets/industries_we_serve/Steel_plant.jpeg';
import cement_industries from '../assets/industries_we_serve/Cement_Industries.jpeg';
import foundries from '../assets/industries_we_serve/Foundries.jpg';
import power_plant from '../assets/industries_we_serve/Power_Plant.jpg';
import sugar_plants from '../assets/industries_we_serve/Sugar_Plants.jpg';
import petro_chemicals from '../assets/industries_we_serve/Petro_Chemicals.jpg';
import ISO_Certificate from '../assets/ISO-Certificate-Janta.jpg';
import home_background from '../assets/new-fire-img-26.jpg';

const Home = () => {
    const [activeIndustry, setActiveIndustry] = useState('Steel Plants');
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    // Slider data
    const slides = [
        {
            image: home_background,
            title: 'JANTA',
            subtitle: 'REFRACTORY',
            description: 'Leading Manufacturer of Premium Quality Refractories in India',
            highlight: 'Excellence in Every Product'
        },
        {
            image: steel_plants,
            title: 'HIGH TEMPERATURE',
            subtitle: 'SOLUTIONS',
            description: 'Advanced Refractory Solutions for Extreme Conditions',
            highlight: 'Built to Withstand Extreme Heat'
        },
        {
            image: cement_industries,
            title: 'INDUSTRIAL',
            subtitle: 'EXCELLENCE',
            description: 'Trusted by Leading Industries Across India',
            highlight: '30+ Years of Industry Leadership'
        },
        {
            image: foundries,
            title: 'QUALITY',
            subtitle: 'ASSURED',
            description: 'ISO Certified Manufacturing with 99% Quality Standards',
            highlight: 'Certified & Trusted Quality'
        }
    ];

    const industries = [
        'Steel Plants', 'Cement Industries', 'Foundries',
        'Power Plant', 'Sugar Plants', 'Petro-Chemicals'
    ];

    const industryData = {
        'Steel Plants': {
            image: steel_plants,
            description: 'Providing high-performance refractories for blast furnaces, ladles, and steel-making equipment. Our products withstand extreme temperatures and chemical attacks in integrated steel plants.'
        },
        'Cement Industries': {
            image: cement_industries,
            description: 'Specialized refractory solutions for cement kilns, preheaters, and coolers. Our products ensure optimal thermal efficiency and extended service life in cement manufacturing processes.'
        },
        'Foundries': {
            image: foundries,
            description: 'Durable refractory materials for melting furnaces, crucibles, and casting operations. Designed to handle molten metal and provide consistent performance in foundry applications.'
        },
        'Power Plant': {
            image: power_plant,
            description: 'Heat-resistant refractories for boilers, furnaces, and thermal equipment. Our solutions ensure energy efficiency and reliable operation in power generation facilities.'
        },
        'Sugar Plants': {
            image: sugar_plants,
            description: 'Acid and alkali resistant refractories for sugar processing equipment, evaporators, and boilers. Ensuring durability and efficiency in sugar manufacturing operations.'
        },
        'Petro-Chemicals': {
            image: petro_chemicals,
            description: 'Chemical-resistant refractories for reactors, crackers, and reformers. Built to withstand corrosive environments and extreme conditions in petrochemical processing.'
        }
    };

    const stats = [
        { icon: Award, number: '30+', label: 'Years Experience' },
        { icon: Users, number: '500+', label: 'Satisfied Clients' },
        { icon: Factory, number: '100+', label: 'Industries Served' },
        { icon: TrendingUp, number: '99%', label: 'Quality Assurance' }
    ];

    // Slider auto-play
    useEffect(() => {
        const timer = setInterval(() => {
            nextSlide();
        }, 2000);
        return () => clearInterval(timer);
    }, [isAnimating]);

    const nextSlide = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentSlide((prev) => (prev + 1) % slides.length);
            setTimeout(() => setIsAnimating(false), 800);
        }
    };

    const prevSlide = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
            setTimeout(() => setIsAnimating(false), 800);
        }
    };

    const goToSlide = (index) => {
        if (!isAnimating && index !== currentSlide) {
            setIsAnimating(true);
            setCurrentSlide(index);
            setTimeout(() => setIsAnimating(false), 800);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            {/* Hero Slider Section */}
            <section className="relative h-[70vh] sm:h-[80vh] lg:h-[90vh] flex items-center justify-center overflow-hidden">
                {/* Background Images */}
                <div className="absolute inset-0 z-0">
                    {slides.map((slide, index) => (
                        <div
                            key={index}
                            className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === index ? 'opacity-100' : 'opacity-0'}`}
                        >
                            <img
                                src={slide.image}
                                alt={slide.title}
                                className="w-full h-full object-cover brightness-90"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-transparent"></div>
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-slate-900/40"></div>
                        </div>
                    ))}
                </div>

                {/* Hero Content - Left Aligned */}
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-xs sm:max-w-md lg:max-w-2xl">
                        {/* Animated Content */}
                        <div key={currentSlide} className="animate-slideIn">
                            <div className="mb-3 sm:mb-4 overflow-hidden">
                                <div className="animate-slideUp" style={{ animationDelay: '0.1s' }}>
                                    <span className="inline-block px-3 py-1 sm:px-4 sm:py-2 bg-orange-500/20 backdrop-blur-sm border border-orange-500/50 rounded-full text-orange-400 text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                                        {slides[currentSlide].highlight}
                                    </span>
                                </div>
                            </div>

                            <div className="overflow-hidden">
                                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-3 animate-slideUp drop-shadow-2xl" style={{ animationDelay: '0.2s' }}>
                                    {slides[currentSlide].title}
                                </h1>
                            </div>

                            <div className="overflow-hidden">
                                <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 animate-slideUp drop-shadow-2xl" style={{ animationDelay: '0.3s' }}>
                                    <span className="text-orange-500">{slides[currentSlide].subtitle}</span>
                                </h2>
                            </div>

                            <div className="overflow-hidden">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8 max-w-xs sm:max-w-md lg:max-w-xl animate-slideUp drop-shadow-lg" style={{ animationDelay: '0.4s' }}>
                                    {slides[currentSlide].description}
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-slideUp" style={{ animationDelay: '0.5s' }}>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Arrows */}
                {/* <button
                    onClick={prevSlide}
                    className="absolute left-2 sm:left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 group"
                    disabled={isAnimating}
                >
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform" />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-2 sm:right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 group"
                    disabled={isAnimating}
                >
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform" />
                </button> */}

                {/* Dot Indicators */}
                <div className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2 sm:gap-3">
                    {slides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`transition-all duration-300 rounded-full ${currentSlide === index
                                ? 'w-8 sm:w-10 lg:w-12 h-2 sm:h-3 bg-orange-500'
                                : 'w-2 h-2 sm:w-3 sm:h-3 bg-white/50 hover:bg-white/80'
                                }`}
                            disabled={isAnimating}
                        />
                    ))}
                </div>

                {/* Slide Counter */}
                <div className="absolute bottom-4 sm:bottom-6 lg:bottom-8 right-2 sm:right-4 lg:right-8 z-20 text-white font-semibold bg-black/30 backdrop-blur-sm px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm">
                    <span className="text-orange-500">{String(currentSlide + 1).padStart(2, '0')}</span>
                    <span className="text-white/50"> / </span>
                    <span className="text-white/70">{String(slides.length).padStart(2, '0')}</span>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-6 sm:py-8 bg-gradient-to-r from-yellow-500 to-orange-500">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center transform hover:scale-105 transition-transform duration-300">
                                <stat.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white mx-auto mb-3 sm:mb-4" />
                                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">{stat.number}</div>
                                <div className="text-sm sm:text-base lg:text-white/90 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-12 sm:py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
                        <div>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                                Welcome <span className="text-orange-600">To Janta Group</span>
                            </h2>
                            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                                JANTA Group is one of the leading refractories manufacturing unit in India producing all range of refractories, Resin Bonded Refractories, Basic Refractories, High Alumina and Fire Clay Refractories catering to the requirements of various furnaces, foundries, integrated steel plants, Non-ferrous Industries, Cement Industries, food & Beverages, Automotive and Petro-Chemicals etc. to their entire satisfaction.
                            </p>
                            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                                JANTA Group professionally managed having technologically advanced manufacturing facility. We at JANTA Group put more emphasis on quality and offer a competitive price for each item we produce. You will be kind enough to appreciate different milestone that our organization has been able to achieve during all these years of hard work since our inception. It won't be out of context to mention here that our top customers with consistent annual orders for the last 30 years and they have appreciated and impressed with our quality.
                            </p>
                            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                                The performance of our product is well established and in-fact has made successful forays to meet the various requirements of integrated steel plant as well as cement industry, Aluminum, Copper etc. with its undisputed quality and reasonable price.
                            </p>
                            <div className="flex items-center gap-3 text-orange-600 font-semibold text-sm sm:text-base">
                                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                                <span>30 Years of Consistent Quality & Service</span>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src={ISO_Certificate}
                                alt="Manufacturing Facility"
                                className="rounded-2xl shadow-2xl w-auto h-96 object-cover mx-auto"
                            />
                            <div className="w-56 h-24 absolute -bottom-16 -left-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-6 rounded-xl shadow-xl">
                                <div className="text-2xl font-bold">ISO Certified</div>
                                <div className="text-sm">Quality Assured</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Industries We Serve */}
            <section className="py-12 sm:py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
                        <div className="order-2 md:order-1 relative">
                            <div className="relative overflow-hidden rounded-2xl shadow-2xl h-[200px] sm:h-[250px] lg:h-[350px]">
                                {Object.entries(industryData).map(([industry, data]) => (
                                    <img
                                        key={industry}
                                        src={data.image}
                                        alt={industry}
                                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${activeIndustry === industry ? 'opacity-100' : 'opacity-0'}`}
                                    />
                                ))}
                            </div>
                            <div className="absolute -bottom-4 sm:-bottom-6 -right-2 sm:-right-4 lg:-right-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-4 sm:p-5 lg:p-6 rounded-xl shadow-xl">
                                <div className="text-lg sm:text-xl lg:text-2xl font-bold">Premium Quality</div>
                                <div className="text-xs sm:text-sm">For Every Industry</div>
                            </div>
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                                Industries We <span className="text-orange-600">Serve</span>
                            </h2>
                            <div className="mb-6 sm:mb-8 min-h-[80px] sm:min-h-[100px]">
                                <p className="text-base sm:text-lg text-gray-700 leading-relaxed transition-all duration-500">
                                    {industryData[activeIndustry].description}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                {industries.map((industry, index) => (
                                    <div
                                        key={index}
                                        onMouseEnter={() => setActiveIndustry(industry)}
                                        className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-all duration-300 ${activeIndustry === industry
                                            ? 'bg-orange-500 text-white shadow-lg scale-105'
                                            : 'bg-gray-50 hover:bg-orange-50 text-gray-800'
                                            }`}
                                    >
                                        <CheckCircle className={`w-5 h-5 flex-shrink-0 transition-colors ${activeIndustry === industry ? 'text-white' : 'text-orange-600'
                                            }`} />
                                        <span className="font-medium">{industry}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">OUR <span className="text-orange-500">PHILOSOPHY</span></h2>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-300 italic">Built on Trust, Quality, and Innovation</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 hover:bg-white/15 transition-all">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-6">
                                <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">VISION</h3>
                            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                                "To establish ourselves as a leader and marketer of refractories and insulation products through focus on our customers, suppliers and employees."
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 hover:bg-white/15 transition-all">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-6">
                                <Rocket className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">MISSION</h3>
                            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                                "To be the customers first choice for delivering products of outstanding quality and great service as a competitive cost by working faster, simpler and to always move together as a team."
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 hover:bg-white/15 transition-all">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-6">
                                <HandCoins className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">VALUES</h3>
                            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                                "Improvement, team work, passion, health & Safety, environmentally friendly, cultural diversity."
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />

            <style jsx>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateX(-50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-slideIn {
                    animation: slideIn 0.8s ease-out forwards;
                }

                .animate-slideUp {
                    animation: slideUp 0.8s ease-out forwards;
                    opacity: 0;
                }

                @media (max-width: 640px) {
                    .absolute.left-2, .absolute.right-2 {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default Home;